import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { useUsage, type ModelStats } from '../../hooks/useUsage';
import { fmt, fmtCost, fmtDate } from '../../utils/format';

function StatCard({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="bg-app-surface border border-app-border rounded-xl p-5 shadow-sm">
      <p className="text-xs text-app-text-dim uppercase tracking-wider mb-1">{label}</p>
      <p className="text-2xl font-semibold text-app-text">{value}</p>
      {sub && <p className="text-xs text-app-text-dim mt-1">{sub}</p>}
    </div>
  );
}

function ModelRow({ model, stats }: { model: string; stats: ModelStats }) {
  const { t } = useTranslation();
  const total = stats.input_tokens + stats.output_tokens;
  const inputPct = total > 0 ? (stats.input_tokens / total) * 100 : 0;
  const outputPct = total > 0 ? (stats.output_tokens / total) * 100 : 0;

  return (
    <div className="py-4 border-b border-app-border/40 last:border-0">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-app-text font-mono truncate">{model}</p>
          <div className="mt-2 flex gap-4 text-xs text-app-text-dim">
            <span>
              <span className="text-app-text font-medium">{fmt(stats.calls)}</span> {t('usage.calls')}
            </span>
            <span>
              <span className="text-app-text font-medium">{fmt(stats.input_tokens)}</span> {t('usage.in')}
            </span>
            <span>
              <span className="text-app-text font-medium">{fmt(stats.output_tokens)}</span> {t('usage.out')}
            </span>
            <span>
              <span className="text-app-text font-medium">{fmt(total)}</span> {t('usage.totalTokensShort')}
            </span>
          </div>
          <div className="mt-2 flex h-1.5 rounded-full overflow-hidden gap-px">
            <div className="bg-app-accent/70 rounded-l-full" style={{ width: `${inputPct}%` }} />
            <div className="bg-app-warning/70 rounded-r-full" style={{ width: `${outputPct}%` }} />
          </div>
        </div>
        <div className="text-right shrink-0">
          <p className="text-base font-semibold text-app-text">{fmtCost(stats.cost_usd)}</p>
          <p className="text-[10px] text-app-text-dim mt-0.5">{t('usage.estimatedCost')}</p>
        </div>
      </div>
    </div>
  );
}

export default function SessionTab() {
  const { t } = useTranslation();
  const { data, loading, error, refresh } = useUsage();

  const totalTokens = data?.total_tokens ?? 0;
  const inputPct = totalTokens > 0 ? ((data?.total_input_tokens ?? 0) / totalTokens) * 100 : 0;
  const outputPct = totalTokens > 0 ? ((data?.total_output_tokens ?? 0) / totalTokens) * 100 : 0;
  const modelCount = data ? Object.keys(data.by_model).length : 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <p className="text-sm text-app-text-dim">{t('usage.sessionIntro')}</p>
        <button
          type="button"
          onClick={refresh}
          disabled={loading}
          className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-app-accent border border-app-border hover:bg-app-muted disabled:opacity-50"
        >
          <ArrowPathIcon className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          {t('common.refresh')}
        </button>
      </div>

      {loading && !data && (
        <p className="text-sm text-app-text-dim py-8 text-center">{t('common.loading')}</p>
      )}

      {error && (
        <div className="bg-app-error/10 border border-app-error/30 rounded-xl p-4 text-sm text-app-error">
          {error}
        </div>
      )}

      {data && (
        <>
          <p className="text-xs text-app-text-dim">
            {t('usage.sessionStarted')}:{' '}
            <span className="text-app-text">{fmtDate(data.session_started_at)}</span>
          </p>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard
              label={t('usage.totalCost')}
              value={fmtCost(data.total_cost_usd)}
              sub={t('usage.estimatedUsd')}
            />
            <StatCard
              label={t('usage.llmCalls')}
              value={fmt(data.total_calls)}
              sub={t('usage.models', { count: modelCount })}
            />
            <StatCard
              label={t('usage.inputTokens')}
              value={fmt(data.total_input_tokens)}
              sub={t('usage.pctOfTotal', { pct: inputPct.toFixed(0) })}
            />
            <StatCard
              label={t('usage.outputTokens')}
              value={fmt(data.total_output_tokens)}
              sub={t('usage.pctOfTotal', { pct: outputPct.toFixed(0) })}
            />
          </div>

          {totalTokens > 0 && (
            <div className="bg-app-surface border border-app-border rounded-xl p-5 shadow-sm">
              <p className="text-sm font-medium text-app-text mb-3">
                {t('usage.totalTokens')}: <span className="font-semibold">{fmt(totalTokens)}</span>
              </p>
              <div className="flex h-3 rounded-full overflow-hidden gap-px">
                <div className="bg-app-accent/70" style={{ width: `${inputPct}%` }} />
                <div className="bg-app-warning/70" style={{ width: `${outputPct}%` }} />
              </div>
            </div>
          )}

          <div className="bg-app-surface border border-app-border rounded-xl p-5">
            <h2 className="text-sm font-medium text-app-text mb-1">{t('usage.breakdownByModel')}</h2>
            <p className="text-xs text-app-text-dim mb-4">{t('usage.pricingNote')}</p>
            {Object.keys(data.by_model).length === 0 ? (
              <p className="text-sm text-app-text-dim py-4">{t('usage.noCallsYet')}</p>
            ) : (
              <div>
                {Object.entries(data.by_model)
                  .sort((a, b) => b[1].cost_usd - a[1].cost_usd)
                  .map(([model, stats]) => (
                    <ModelRow key={model} model={model} stats={stats} />
                  ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
